import React from "react";
import { formatBook } from "../../../utils/formatOdds";

const IMG = `${import.meta.env.BASE_URL}img/cards`;

const ODD_CARDS = ["A", "3", "5", "7", "9"];
const EVEN_CARDS = ["2", "4", "6", "8", "10"];
const LOW_CARDS = ["A", "2", "3", "4", "5"];
const HIGH_CARDS = ["6", "7", "8", "9", "10"];

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);
const bySno = (a, b) => (Number(a?.sno) || 0) - (Number(b?.sno) || 0);

// Reference: a box is suspended whenever gstatus is not OPEN.
const isSuspended = (item) => String(item?.gstatus || "").toUpperCase() !== "OPEN";

/**
 * Note Number bet table — reference markup (inside .casino-detail of `.casino-table.note`):
 *   container-fluid > row.row5.d-none-small > 6 × col-4 (Odd, Black, Low, Even, Red, High)
 *   container-fluid > row.row5 > col-md-4.note-baccarat (Baccarat 1/2) + col-md-8.note-cards (Card A..10)
 * Book values follow the reference v-casinofancybook directive: book-green when > 0 else book-red.
 * A locked Baccarat cell that shows a book pins its padlock to the top ("lock-top").
 */
export default function BetTableNotEnum({ data, onBetClick, exposures = {} }) {
  const sub = Array.isArray(data?.sub) ? data.sub : [];
  const pick = (subtype) => sub.filter((s) => s?.subtype === subtype).sort(bySr);

  const odd = pick("odd")[0] || {};
  const even = pick("even")[0] || {};
  const black = pick("black")[0] || {};
  const red = pick("red")[0] || {};
  const low = pick("low")[0] || {};
  const high = pick("high")[0] || {};
  const bacc = pick("bacc");
  const card = pick("card")[0] || {};

  const getExposure = (nat) => {
    if (!nat) return 0;
    const exp = exposures[nat] ?? exposures[String(nat).toLowerCase()] ?? exposures[String(nat).trim()] ?? 0;
    return Number(exp) || 0;
  };

  const book = (nat) => {
    const exp = getExposure(nat);
    return {
      cls: exp > 0 ? "book-green" : "book-red",
      text: formatBook(exp),
    };
  };

  const click = (item, type, rate, nat = item?.nat, payload = item) => {
    if (isSuspended(item) || !rate || !onBetClick) return;
    onBetClick(`${nat} ${type === "lay" ? "Lay" : "Back"}`, rate, type, payload);
  };

  const cardImages = (list) => (
    <div className="d-flex">
      {list.map((c, i) => (
        <React.Fragment key={c}>
          {i > 0 ? " " : null}
          <div className="card-image ml-1">
            <img src={`${IMG}/single/${c}.jpg`} />
          </div>
        </React.Fragment>
      ))}
    </div>
  );

  const suitImages = (a, b) => (
    <div className="casino-card-img">
      <span>
        <img src={`${IMG}/${a}.png`} /> <img src={`${IMG}/${b}.png`} />
      </span>
    </div>
  );

  const box = (item, label, colClass) => {
    const locked = isSuspended(item);
    const bk = book(item?.nat);
    return (
      <div className={colClass}>
        <div className="casino-box-row">
          <div className="casino-nation-name">{label}</div>{" "}
          <div className="casino-bl-box">
            <div className={`back casino-bl-box-item${locked ? " suspended" : ""}`} onClick={() => click(item, "back", item?.b)}>
              <span className="casino-box-odd">{item?.b}</span>
            </div>{" "}
            <div className={`lay casino-bl-box-item${locked ? " suspended" : ""}`} onClick={() => click(item, "lay", item?.l)}>
              <span className="casino-box-odd">{item?.l}</span>
            </div>
          </div>{" "}
          <div className={`casino-nation-name ${bk.cls}`}>{bk.text}</div>
        </div>
      </div>
    );
  };

  const named = (item, list) => (
    <>
      <b>{item?.nat}</b> {cardImages(list)}
    </>
  );

  const baccRow = (item, hint) => {
    const locked = isSuspended(item);
    const bk = book(item?.nat);
    return (
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b>{item?.nat}</b> <span className="text-yellow">{hint}</span>
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${locked ? " suspended lock-top" : ""}`}
            onClick={() => click(item, "back", item?.b)}
          >
            <span className="casino-box-odd">{item?.b}</span> <span className={bk.cls}>{bk.text}</span>
          </div>
        </div>
      </div>
    );
  };

  const cardOdds = Array.isArray(card?.odds) ? [...card.odds].sort(bySno) : [];
  const cardLocked = isSuspended(card);

  return (
    <>
      <div className="container-fluid container-fluid-5">
        <div className="row row5 d-none-small">
          {box(odd, named(odd, ODD_CARDS), "col-4 oe-cards")}{" "}
          {box(black, suitImages("spade", "club"), "col-4")}{" "}
          {box(low, named(low, LOW_CARDS), "col-4 oe-cards")}{" "}
          {box(even, named(even, EVEN_CARDS), "col-4 oe-cards")}{" "}
          {box(red, suitImages("heart", "diamond"), "col-4")}{" "}
          {box(high, named(high, HIGH_CARDS), "col-4 oe-cards")}
        </div>
      </div>{" "}
      <div className="container-fluid container-fluid-5">
        <div className="row row5">
          <div className="col-md-4 col-12 note-baccarat">
            {baccRow(bacc[0] || {}, "(1st, 2nd, 3rd card)")}{" "}
            {baccRow(bacc[1] || {}, "(4th, 5th, 6th card)")}
          </div>{" "}
          <div className="col-md-8 col-12 note-cards">
            <div className="casino-cards text-center mt-1">
              {cardOdds.map((o, i) => {
                // Card bets are stored "<live Card N> <value>" ("Card 4 A"): only the live
                // position's figure shows, and it goes once the counter moves on.
                const value = String(o?.nat || "").replace(/^Card\s*/i, "").trim();
                const bk = book(card?.nat && value ? `${card.nat} ${value}` : "");
                const img = String(o?.ssid) === "1" ? "A" : o?.ssid;
                return (
                  <div className="casino-card-item" key={o?.ssid ?? i}>
                    <div className="casino-book">{o?.b}</div>{" "}
                    <div
                      className={`card-image${cardLocked ? " suspended" : ""}`}
                      onClick={() => click(card, "back", o?.b, o?.nat, { ...o, subtype: "card", sid: card?.sid, mid: data?.mid })}
                    >
                      <img src={`${IMG}/${img}.png`} />
                    </div>{" "}
                    <div className={bk.cls}>{bk.text}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
